import { Navbar } from "@/components/navbar"
import { Footer } from "@/components/footer"
import { PolicyHeader } from "@/components/shared"
import { companyDetails } from "@/lib/company-details"

const effectiveDate = "April 12, 2026"

const sections = [
  {
    id: "scope",
    title: "Scope and Overview",
    content:
      `${companyDetails.legalName}, trading as Monra ("Monra," "we," "our," or "us"), collects, uses, discloses, and protects personal information when you use Monra's website, mobile applications, customer support channels, and products, including Monra eSIM. Where a specific Monra product requires additional disclosures, operational notices, or regulatory terms, those materials may supplement this policy.`,
  },
  {
    id: "information-we-collect",
    title: "Information We Collect",
    content:
      "The information we collect depends on how you interact with Monra, the plan you purchase, and the legal requirements that apply in the countries where we operate.",
    bullets: [
      "Identity and contact information, such as your name, email address, phone number, billing address, and customer support communications.",
      "Account and transaction data, such as order history, payment confirmations, plan selections, recharge activity, invoices, and refund history.",
      "Device and technical data, such as IP address, device model, operating system, app version, device identifiers, and eSIM-related identifiers such as EID, ICCID, or similar provisioning references.",
      "Usage and service data, such as destination country, activation events, network partner details, top-up activity, service status, diagnostics, and data-consumption records needed to deliver and secure the service.",
      "Verification and compliance data, such as date of birth, address, or government-issued identification where required by applicable law, sanctions screening, anti-fraud checks, SIM-registration rules, or telecom regulations.",
      "Website and cookie data, such as browser settings, session data, referral pages, and analytics data collected through cookies or similar technologies.",
    ],
  },
  {
    id: "how-we-use-information",
    title: "How We Use Information",
    content:
      "We use personal information only where we have a lawful reason to do so and where it is relevant to operating Monra's website and products safely and effectively.",
    bullets: [
      "To create and administer your account, deliver purchased eSIM plans, and enable activation, recharge, and support services.",
      "To verify device compatibility, troubleshoot activation issues, monitor service quality, and prevent abuse of network resources.",
      "To process payments, detect fraud, enforce our Terms of Use and applicable product-specific service terms, and investigate suspicious activity, chargebacks, or account misuse.",
      "To comply with tax, sanctions, anti-money-laundering, lawful intercept, SIM registration, consumer protection, and telecom obligations that apply to our business or our network partners.",
      "To communicate with you about purchases, security events, service notices, policy changes, or promotional messages where permitted by law.",
      "To analyze platform performance, improve product design, develop new features, and maintain the security and reliability of our website, app, and provisioning systems.",
    ],
  },
  {
    id: "legal-bases",
    title: "Legal Bases for Processing",
    content:
      "Where privacy laws require a legal basis for processing, Monra generally relies on one or more of the following:",
    bullets: [
      "Contract performance, when we need your information to provide the services you requested or to support your account.",
      "Legal obligation, when laws or regulators require us or our partners to collect, verify, retain, or disclose certain information.",
      "Legitimate interests, when processing is reasonably necessary to secure our systems, prevent fraud, improve service quality, or manage our business in a proportionate way.",
      "Consent, where required by law, such as for certain marketing messages or non-essential cookies.",
    ],
  },
  {
    id: "sharing",
    title: "How We Share Information",
    content:
      "We do not sell personal information for monetary consideration. We share information only as needed to operate the service, comply with the law, or protect Monra and our users.",
    bullets: [
      "Telecom, carrier, and provisioning partners that enable eSIM delivery, plan activation, roaming, and network access.",
      "Payment processors, fraud-screening providers, sanctions-screening vendors, and billing support providers.",
      "Cloud hosting, analytics, customer support, communications, and security vendors acting on our instructions.",
      "Professional advisers, auditors, insurers, or transaction counterparties in connection with financing, restructuring, merger, acquisition, or asset sale activity.",
      "Courts, regulators, law enforcement, or other third parties when disclosure is required by law, subpoena, legal process, network-security obligation, or to protect rights, safety, and property.",
    ],
  },
  {
    id: "international-transfers",
    title: "International Transfers",
    content:
      "Monra operates across borders and may transfer personal information to countries other than the one where you live, including to countries where our vendors, support teams, or telecom partners operate. Where required by law, we use appropriate safeguards for international data transfers. However, privacy protections may differ by jurisdiction, and local regulators or network partners may require additional disclosures or retention.",
  },
  {
    id: "retention",
    title: "Data Retention",
    content:
      "We retain personal information only for as long as reasonably necessary to provide the service, resolve disputes, maintain records, prevent fraud, comply with legal and regulatory duties, and enforce our contracts. Retention periods vary depending on the type of data, the plan purchased, billing rules, applicable telecom obligations, and whether an account is under review. Even after deletion requests, certain logs, backup copies, or compliance records may be retained where permitted or required by law.",
  },
  {
    id: "cookies",
    title: "Cookies and Similar Technologies",
    content:
      "We use cookies and similar technologies to keep the website functional, remember preferences, understand site usage, detect abuse, and measure performance. Some cookies are necessary for the site to operate, while others may support analytics or communications. You can manage cookies through your browser settings, but disabling certain cookies may affect functionality.",
  },
  {
    id: "security",
    title: "Security",
    content:
      "We use administrative, technical, and organizational safeguards that are appropriate to the nature of the data and the risks involved. These measures may include encryption in transit, access controls, logging, vendor controls, least-privilege access, and fraud-monitoring processes. No online service can guarantee absolute security, and you are responsible for protecting your account credentials, device access, and any QR codes or installation details provided to you.",
  },
  {
    id: "rights",
    title: "Your Rights and Choices",
    content:
      "Depending on where you live, you may have the right to access, correct, delete, restrict, object to, or port certain personal information, or to withdraw consent where consent is the basis for processing.",
    bullets: [
      "You may update certain account details directly through your account or by contacting support.",
      "You may opt out of marketing emails by using the unsubscribe link or contacting us.",
      "If you are in the EEA or UK, you may also have the right to complain to your local data protection authority.",
      "If you are a California resident and applicable law covers your data, you may have rights to know, delete, correct, and receive equal service for exercising those rights.",
      "We may need to verify your identity before fulfilling a request, and we may decline requests where an exemption or legal obligation applies.",
    ],
  },
  {
    id: "children",
    title: "Children's Privacy",
    content:
      "Monra's services are not directed to children under 16, and we do not knowingly collect personal information from children under 16 without appropriate authorization. If you believe a child has provided personal information to us in violation of this policy, contact us so we can review and take appropriate action.",
  },
  {
    id: "changes",
    title: "Changes to This Policy",
    content:
      "We may update this Privacy Policy to reflect changes in our services, legal obligations, vendors, or data practices. If we make material changes, we may post a notice in the app or on the website, update the effective date above, or provide additional notice where required by law. Your continued use of the service after an updated policy takes effect means the updated policy will apply to future use.",
  },
  {
    id: "contact",
    title: "Contact Us",
    content:
      "For privacy questions, rights requests, or complaints, contact Monra support. Please include enough information for us to identify your account and the nature of your request.",
    email: companyDetails.contactEmail,
  },
]

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background animate-fade-in">
      <Navbar links={[]} logoVariant="main" logoBehavior="navigate" isTransparent={false} />

      <PolicyHeader
        title="Privacy Policy"
        subtitle="How Monra handles personal data across its website, support channels, and products."
        effectiveDate={effectiveDate}
      />

      <section className="bg-background py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="legal-card mb-10">
            <h2 className="mb-4 font-sans text-lg font-semibold text-foreground">Table of Contents</h2>
            <ol className="space-y-2 text-sm text-muted-foreground">
              {sections.map((section, index) => (
                <li key={section.id} className="flex gap-3">
                  <span className="w-6 shrink-0 tabular-nums text-foreground/45">{String(index + 1).padStart(2, "0")}</span>
                  <a href={`#${section.id}`} className="transition-colors hover:text-foreground">
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {sections.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-20 border-t border-border py-10">
              <h2 className="mb-3 font-sans text-xl font-semibold tracking-tight text-foreground">{section.title}</h2>
              <p className="leading-relaxed text-muted-foreground">{section.content}</p>
              {"bullets" in section && section.bullets && (
                <ul className="mt-4 space-y-2 text-muted-foreground">
                  {section.bullets.map((bullet, index) => (
                    <li key={index} className="list-disc pl-5">
                      {bullet}
                    </li>
                  ))}
                </ul>
              )}
              {"email" in section && section.email && (
                <a
                  href={`mailto:${section.email}`}
                  className="mt-2 inline-block font-semibold text-primary transition-colors hover:underline"
                >
                  {section.email}
                </a>
              )}
            </section>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  )
}
